/**
 * 代行アクセス用 組織スイッチャ (ヘッダ右に常駐)
 *
 * - partner admin / 外部委託デザイナー (= canImpersonate) のみ表示
 * - 通常時: 「自組織」表記 (グレー)
 * - 代行中: 対象 org の slug 表記 (オレンジ強調)
 * - クリックで展開: 代行可能な組織一覧 + 「終了」ボタン (代行中のみ)
 * - 一覧が 10 件以上なら検索ボックスを自動表示
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../services/apiClient';
import { useUser } from '../auth/useUser';
import { useImpersonation } from './useImpersonation';
import { hardNavigate } from '../../utils/navigation';
import './ImpersonationOrgSwitcher.css';

interface Target {
  id: string;
  slug?: string | null;
  name?: string | null;
  display_name?: string | null;
  is_favorite?: boolean;
}

interface TargetsResponse {
  targets: Target[];
}

const TARGETS_QUERY_KEY = ['impersonation-switcher-targets'] as const;

const SEARCH_THRESHOLD = 10;

export function ImpersonationOrgSwitcher() {
  const { canImpersonate, isAuthenticated } = useUser();
  const { currentSession, start, end, isImpersonating } = useImpersonation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [busyTargetId, setBusyTargetId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: TARGETS_QUERY_KEY,
    queryFn: async () => {
      try {
        const res = await apiClient.get<TargetsResponse>('/impersonation/targets');
        return res.data.targets ?? [];
      } catch {
        return [] as Target[];
      }
    },
    enabled: isAuthenticated && canImpersonate,
    staleTime: 60_000,
  });

  const targets = query.data ?? [];

  /**
   * お気に入りトグル。楽観的更新でリスト上の星マークとソート順を即時反映し、
   * その後 invalidate でサーバー側状態に揃える。
   */
  const toggleFavorite = useMutation({
    mutationFn: async ({ targetId, currentIsFav }: { targetId: string; currentIsFav: boolean }) => {
      if (currentIsFav) {
        await apiClient.delete(`/impersonation/favorites/${targetId}`);
      } else {
        await apiClient.post('/impersonation/favorites', { target_organization_id: targetId });
      }
    },
    onMutate: async ({ targetId, currentIsFav }) => {
      await queryClient.cancelQueries({ queryKey: TARGETS_QUERY_KEY });
      const prev = queryClient.getQueryData<Target[]>(TARGETS_QUERY_KEY);
      if (prev) {
        const updated = prev.map((t) =>
          t.id === targetId ? { ...t, is_favorite: !currentIsFav } : t,
        );
        // お気に入りを先頭にソート (グループ内順序は維持 = stable sort)
        updated.sort((a, b) => Number(!!b.is_favorite) - Number(!!a.is_favorite));
        queryClient.setQueryData(TARGETS_QUERY_KEY, updated);
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(TARGETS_QUERY_KEY, ctx.prev);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: TARGETS_QUERY_KEY });
    },
  });

  // 外側クリックで閉じる
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // 代行状態が変わったら検索/エラーをリセット
  useEffect(() => {
    setSearch('');
    setError(null);
  }, [currentSession?.id]);

  const filtered = useMemo(() => {
    if (!search.trim()) return targets;
    const q = search.trim().toLowerCase();
    return targets.filter((t) => {
      return [t.slug, t.display_name, t.name, t.id]
        .filter(Boolean)
        .some((s) => String(s).toLowerCase().includes(q));
    });
  }, [targets, search]);

  if (!isAuthenticated || !canImpersonate) return null;

  // 代行中: 代行対象 org の slug を表示 (slug が無ければ display_name → name → 短縮 ID)。
  // ※ useUser の JWT は actor 側の slug を持つため、必ず currentSession を優先する。
  // 通常時: 自組織であることを示す固定ラベル。
  const finalTriggerLabel = isImpersonating
    ? currentSession?.target_organization?.slug ||
      currentSession?.target_organization?.display_name ||
      currentSession?.target_organization?.name ||
      currentSession?.target_organization_id.slice(0, 8) ||
      '代行中'
    : '自組織';

  // 組織切り替え後は react-query キャッシュや素材パネル等のローカル状態を確実に
  // リセットするため、ホームへハードナビゲートする (= 完全リロード)。
  // basename (/app 等) を取りこぼさないよう hardNavigate を経由する。
  const hardNavigateHome = () => hardNavigate('/');

  const handleSelect = async (t: Target) => {
    setError(null);
    if (isImpersonating && currentSession?.target_organization_id === t.id) {
      setOpen(false);
      return;
    }
    setBusyTargetId(t.id);
    try {
      // 既に他組織で代行中の場合は一旦終了してから新規開始
      if (isImpersonating) {
        await end();
      }
      await start({ targetOrganizationId: t.id });
      setOpen(false);
      hardNavigateHome();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string; error?: string } }; message?: string };
      setError(
        e?.response?.data?.detail ||
          e?.response?.data?.error ||
          e?.message ||
          '代行の開始に失敗しました',
      );
    } finally {
      setBusyTargetId(null);
    }
  };

  const handleEnd = async () => {
    setError(null);
    try {
      await end();
      setOpen(false);
      hardNavigateHome();
    } catch (err: unknown) {
      const e = err as { message?: string };
      setError(e?.message || '代行の終了に失敗しました');
    }
  };

  const showSearch = targets.length >= SEARCH_THRESHOLD;

  return (
    <div
      ref={containerRef}
      className={`imp-switcher${isImpersonating ? ' is-impersonating' : ''}`}
    >
      <button
        type="button"
        className="imp-switcher-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={isImpersonating ? `${finalTriggerLabel} を代行中` : '代行する組織を選択'}
      >
        <span className="imp-switcher-label">{finalTriggerLabel}</span>
        <span className="imp-switcher-caret" aria-hidden="true">▾</span>
      </button>
      {open && (
        <div className="modal-surface imp-switcher-menu" role="listbox">
          {isImpersonating && (
            <>
              <button
                type="button"
                className="imp-switcher-item is-end"
                onClick={handleEnd}
              >
                <span className="imp-switcher-item-name">自組織に戻る</span>
              </button>
              <div className="imp-switcher-divider" />
            </>
          )}
          {showSearch && (
            <div className="imp-switcher-search">
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="検索 (slug / 名称)"
                autoFocus
              />
            </div>
          )}
          <div className="imp-switcher-list">
            {query.isLoading ? (
              <div className="imp-switcher-empty">読み込み中…</div>
            ) : filtered.length === 0 ? (
              <div className="imp-switcher-empty">
                {targets.length === 0
                  ? '代行可能な組織はありません'
                  : '一致する組織が見つかりません'}
              </div>
            ) : (
              filtered.map((t) => {
                const isCurrent =
                  isImpersonating && currentSession?.target_organization_id === t.id;
                const mainLabel = t.display_name || t.name || t.slug || t.id.slice(0, 8);
                const subLabel =
                  t.slug && (t.display_name || t.name) ? t.slug : null;
                const isFav = !!t.is_favorite;
                return (
                  <div
                    key={t.id}
                    className={`imp-switcher-item-row${isCurrent ? ' is-current' : ''}`}
                  >
                    <button
                      type="button"
                      className="imp-switcher-item"
                      onClick={() => handleSelect(t)}
                      disabled={busyTargetId !== null || isCurrent}
                      aria-current={isCurrent ? 'true' : undefined}
                    >
                      <span className="imp-switcher-item-name">{mainLabel}</span>
                      {subLabel && (
                        <span className="imp-switcher-item-slug">{subLabel}</span>
                      )}
                      {isCurrent && (
                        <span className="imp-switcher-item-current">現在</span>
                      )}
                    </button>
                    <button
                      type="button"
                      className={`imp-switcher-item-fav${isFav ? ' is-on' : ''}`}
                      onClick={() => toggleFavorite.mutate({ targetId: t.id, currentIsFav: isFav })}
                      aria-label={isFav ? 'お気に入りから外す' : 'お気に入りに登録'}
                      aria-pressed={isFav}
                      title={isFav ? 'お気に入りから外す' : 'お気に入りに登録'}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill={isFav ? 'currentColor' : 'none'}
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                      </svg>
                    </button>
                  </div>
                );
              })
            )}
          </div>
          {error && <p className="imp-switcher-error" role="alert">{error}</p>}
        </div>
      )}
    </div>
  );
}
